// Structured product catalogue. This mirrors what a future WHMCS API
// integration would return, so cards can switch data sources later
// without changing component code.
//
// IMPORTANT: never invent prices or specifications here. Use placeholders
// (marked below) until real catalogue data is confirmed, and set
// `available: false` for anything not yet ready to sell.

import { whmcs } from "@/config/whmcs";

export type ProductCategory = "vps" | "dedicated" | "cloud" | "email" | "security";

export type Product = {
  id: string;
  name: string;
  category: ProductCategory;
  description: string;
  price?: string; // omit entirely if unconfirmed — components must hide price when undefined
  billingCycle?: string;
  specifications: { label: string; value: string }[];
  featured?: boolean;
  available: boolean;
  buyNowUrl: string;
};

// PLACEHOLDER DATA — replace with confirmed specs/pricing before launch.
export const vpsPlans: Product[] = [
  {
    id: "vps-starter",
    name: "VPS Starter",
    category: "vps",
    description: "A practical starting point for smaller sites and development environments.",
    specifications: [
      { label: "vCPU", value: "TBC" },
      { label: "RAM", value: "TBC" },
      { label: "Storage", value: "TBC" },
      { label: "Bandwidth", value: "TBC" },
      { label: "OS options", value: "Linux / Windows" },
    ],
    available: false,
    buyNowUrl: `${whmcs.storeUrl}/vps-starter`,
  },
  {
    id: "vps-growth",
    name: "VPS Growth",
    category: "vps",
    description: "More headroom for growing websites, applications and small platforms.",
    specifications: [
      { label: "vCPU", value: "TBC" },
      { label: "RAM", value: "TBC" },
      { label: "Storage", value: "TBC" },
      { label: "Bandwidth", value: "TBC" },
      { label: "OS options", value: "Linux / Windows" },
    ],
    featured: true,
    available: false,
    buyNowUrl: `${whmcs.storeUrl}/vps-growth`,
  },
  {
    id: "vps-pro",
    name: "VPS Pro",
    category: "vps",
    description: "For resource-intensive workloads that still don't need a full dedicated server.",
    specifications: [
      { label: "vCPU", value: "TBC" },
      { label: "RAM", value: "TBC" },
      { label: "Storage", value: "TBC" },
      { label: "Bandwidth", value: "TBC" },
      { label: "OS options", value: "Linux / Windows" },
    ],
    available: false,
    buyNowUrl: `${whmcs.storeUrl}/vps-pro`,
  },
];

export const dedicatedPlans: Product[] = [
  {
    id: "dedicated-standard",
    name: "Dedicated Standard",
    category: "dedicated",
    description: "Dedicated resources for a single, demanding workload.",
    specifications: [
      { label: "Processor", value: "TBC" },
      { label: "Cores / Threads", value: "TBC" },
      { label: "RAM", value: "TBC" },
      { label: "Storage", value: "TBC" },
      { label: "Network port", value: "TBC" },
      { label: "Location", value: "TBC" },
    ],
    available: false,
    buyNowUrl: `${whmcs.storeUrl}/dedicated-standard`,
  },
];

export const emailPlan: Product = {
  id: "email-30gb",
  name: "30 GB Business Mailbox",
  category: "email",
  description:
    "A professional business email solution with a 30 GB mailbox and advanced everyday features.",
  price: "₹2,200",
  // billingCycle intentionally omitted — confirm in WHMCS catalogue before displaying
  specifications: [
    { label: "Mailbox storage", value: "30 GB" },
    { label: "Access", value: "Multi-device" },
    { label: "Identity", value: "Professional business domain" },
  ],
  featured: true,
  available: true,
  buyNowUrl: `${whmcs.storeUrl}/business-email`,
};

export function getFeatured(products: Product[]) {
  return products.filter((p) => p.featured);
}
